import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import {
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import useAuth from 'hooks/useAuth';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { useMemo } from 'react';
import { useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { useParams } from 'react-router-dom';
import { useGetPartyGroupByIdQuery } from 'store/api/groups';
import { useGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { canEditGroup } from './groupFormMapping';
import { GroupPageHeader } from './GroupPageHeader';

import CasesView from './views/CasesView';
import EffortsView from './views/EffortsView';
import GeneralView from './views/GeneralView';
import ReferralsView from './views/ReferralsView';

const UPDATE_ALL = ['UPDATE.PARTY_GROUPS.ALL'];
const UPDATE_MY_OWN = ['UPDATE.PARTY_GROUPS.MY_OWN'];

const GroupPage = (): JSX.Element => {
    const { id: groupId } = useParams();
    const isNew = useIsNewDesign();
    const { user } = useAuth();
    const canUpdateAll = useHasPermissions(UPDATE_ALL);
    const canUpdateMyOwn = useHasPermissions(UPDATE_MY_OWN);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: partyGroup, isLoading } = useGetPartyGroupByIdQuery(groupId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!partyGroup?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/groups');
    const draftActions = useDraftActions({
        draft: partyGroup?.isDraft ? { type: 'group', id: partyGroup.id } : null,
        createRoute: '/group/new',
        onDeleted: leaveDeleted,
    });

    const { data: officeListData, isLoading: isLoadingOfficeList } = useGetMinOfficesWithFiltersQuery(
        { page: 0, pageSize: 2000 },
        {
            skip: groupId === undefined,
        }
    );

    const { data: teamListData, isLoading: isLoadingTeamListData } = useGetMinTeamsListWithFiltersQuery({
        page: 0,
        pageSize: 2000,
        filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
    });
    const practiceGroupList = useMemo(() => teamListData?.content || [], [teamListData]);

    const officeList = useMemo(() => officeListData?.content || [], [officeListData]);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['GROUP_TYPE', 'GROUP_PARTNERSHIP_TYPE', 'BUSINESS_AREA_TYPE', 'SEGMENT_TYPE', 'SERVICE_LEVEL_TYPE'],
    });

    const tabComponents = useMemo(
        (): TabType[] =>
            partyGroup
                ? [
                      {
                          id: 'general',
                          label: t('groups.general'),
                          component: (
                              <GeneralView
                                  classifiers={classifiers}
                                  partyGroup={partyGroup}
                                  officeList={officeList}
                                  teamList={practiceGroupList}
                                  isNew={isNew}
                              />
                          ),
                      },
                      {
                          id: 'cases',
                          label: t('groups.cases'),
                          // A draft group has no cases, referrals or efforts of its own.
                          hidden: isDraft,
                          component: <CasesView partyGroup={partyGroup} />,
                      },
                      {
                          id: 'referrals',
                          label: t('groups.referrals'),
                          hidden: isDraft,
                          component: <ReferralsView partyGroup={partyGroup} />,
                      },
                      {
                          id: 'efforts',
                          label: t('groups.efforts'),
                          hidden: isDraft,
                          component: <EffortsView partyGroup={partyGroup} />,
                      },
                  ]
                : [],
        [officeList, partyGroup, classifiers, practiceGroupList, isNew, isDraft]
    );

    if (isLoading || isLoadingClassifiers || isLoadingOfficeList || isLoadingTeamListData) {
        return <Loader />;
    }

    if (!partyGroup) {
        return <></>;
    }

    if (isNew) {
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage header={<GroupPageHeader partyGroup={partyGroup} classifiers={classifiers} isNew />}>
                    <SectionEditProvider
                        canEdit={!isDraft && canEditGroup(partyGroup, user?.id, { canUpdateAll, canUpdateMyOwn })}
                    >
                        <TabsGallery
                            tabs={tabComponents}
                            tabGroupId="group-page"
                            trackTabsInHistoryState
                            usePathBasedRouting
                            wrapperClassName={RECORD_TABS_CLASS}
                            panelClassName={RECORD_PANEL_CLASS}
                            className={RECORD_TAB_STRIP_CLASS}
                        />
                    </SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    // The header and the General view read the draft from here.
    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout>
                <GroupPageHeader partyGroup={partyGroup} classifiers={classifiers} />
                <TabsGallery tabs={tabComponents} tabGroupId="group-page" trackTabsInHistoryState usePathBasedRouting />
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default GroupPage;
